import {
  ConditionDto,
  ParcelDto,
  PickupPointDto,
  PublicShopDto,
  TrackingStatusChangeTriggerConfigDto,
} from "@babackapp/private-api-client/types";
import type { WorkflowTriggerType } from "@babackapp/variables-registry";
import { CheckIcon, XIcon } from "lucide-react";

const ALL_TRIGGERS: readonly WorkflowTriggerType[] = ["tracking_status_change", "order_not_fulfilled", "order_created"];
const TRACKING_ONLY: readonly WorkflowTriggerType[] = ["tracking_status_change"];

export interface TrackingStatusChangeEventData {
  parcel: ParcelDto;
  order?: {
    tags: {
      shopify: string[];
      internal: string[];
    };
  } | null;
  shop: PublicShopDto;
  pickupPoint?: PickupPointDto;
  status: TrackingStatusChangeTriggerConfigDto["trackingStatus"];
  periods: {
    christmas: boolean;
    blackFriday: boolean;
  };
  isPartialShipment: boolean;
}

export type ConditionOperator =
  | ConditionDto["operator"]
  | "array_contains_any"
  | "array_contains_all";

type PropertyNames<T> = T extends object
  ? {
      [K in string & keyof T]: K | `${K & string}.${PropertyNames<T[K]>}`;
    }[string & keyof T]
  : never;

type EventDataPath = PropertyNames<TrackingStatusChangeEventData>;

export const ARRAY_OPERATORS: ReadonlyArray<ConditionOperator> = [
  "in",
  "not_in",
  "array_contains_any",
  "array_contains_all",
];

export type ConditionFieldDefinition = {
  value: EventDataPath;
  label: string;
  defaultLabel?: string;
  operators: ReadonlyArray<ConditionOperator>;
  triggers: readonly WorkflowTriggerType[];
};

export const conditionFields: ReadonlyArray<ConditionFieldDefinition> = [
  {
    value: "parcel.giftConfig.activated",
    label: "workflowsEditor.node.condition.fields.parcel.giftConfig.activated",
    operators: ["is_true", "is_false"],
    triggers: TRACKING_ONLY,
  },
  {
    value: "periods.christmas",
    label: "workflowsEditor.node.condition.fields.periods.christmas",
    operators: ["is_true", "is_false"],
    triggers: TRACKING_ONLY,
  },
  {
    value: "periods.blackFriday",
    label: "workflowsEditor.node.condition.fields.periods.blackFriday",
    operators: ["is_true", "is_false"],
    triggers: TRACKING_ONLY,
  },
  {
    value: "parcel.id",
    label: "workflowsEditor.node.condition.fields.parcel.id",
    operators: ["equals", "not_equals"],
    triggers: TRACKING_ONLY,
  },
  {
    value: "parcel.trackingId",
    label: "workflowsEditor.node.condition.fields.parcel.trackingId",
    operators: ["equals", "not_equals", "contains"],
    triggers: TRACKING_ONLY,
  },
  {
    value: "parcel.customerLocale",
    label: "workflowsEditor.node.condition.fields.parcel.customerLocale",
    operators: ["equals", "not_equals", "in", "not_in"],
    triggers: TRACKING_ONLY,
  },
  {
    value: "parcel.carrierCode",
    label: "workflowsEditor.node.condition.fields.parcel.carrierCode",
    operators: ["equals", "not_equals", "in", "not_in"],
    triggers: TRACKING_ONLY,
  },
  {
    value: "parcel.destination.city",
    label: "workflowsEditor.node.condition.fields.parcel.destination.city",
    operators: ["equals", "not_equals", "contains"],
    triggers: TRACKING_ONLY,
  },
  {
    value: "parcel.destination.country",
    label: "workflowsEditor.node.condition.fields.parcel.destination.country",
    operators: ["equals", "not_equals", "in", "not_in"],
    triggers: TRACKING_ONLY,
  },
  {
    value: "pickupPoint.city",
    label: "workflowsEditor.node.condition.fields.pickupPoint.city",
    operators: ["equals", "not_equals", "contains"],
    triggers: TRACKING_ONLY,
  },
  {
    value: "pickupPoint.name",
    label: "workflowsEditor.node.condition.fields.pickupPoint.name",
    operators: ["equals", "not_equals", "contains"],
    triggers: TRACKING_ONLY,
  },
  {
    value: "pickupPoint.externalId",
    label: "workflowsEditor.node.condition.fields.pickupPoint.externalId",
    operators: ["equals", "not_equals"],
    triggers: TRACKING_ONLY,
  },
  {
    value: "order.tags",
    label: "workflowsEditor.node.condition.fields.order.tags",
    defaultLabel: "Tags de commande",
    operators: ["array_contains_any", "array_contains_all"],
    triggers: ALL_TRIGGERS,
  },
  {
    value: "isPartialShipment",
    label: "workflowsEditor.node.condition.fields.isPartialShipment",
    defaultLabel: "Expédition partielle",
    operators: ["is_true", "is_false"],
    triggers: TRACKING_ONLY,
  },
] as const;

export const OPERATORS: {
  value: ConditionOperator;
  label: string;
  defaultLabel?: string;
  symbol: string | React.ReactNode;
  noValue?: boolean;
}[] = [
  {
    value: "equals",
    label: "workflowsEditor.node.condition.operators.equals",
    symbol: "=",
  },
  {
    value: "not_equals",
    label: "workflowsEditor.node.condition.operators.not_equals",
    symbol: "≠",
  },
  {
    value: "greater_than",
    label: "workflowsEditor.node.condition.operators.greater_than",
    symbol: ">",
  },
  {
    value: "less_than",
    label: "workflowsEditor.node.condition.operators.less_than",
    symbol: "<",
  },
  {
    value: "contains",
    label: "workflowsEditor.node.condition.operators.contains",
    symbol: "⊃",
  },
  {
    value: "in",
    label: "workflowsEditor.node.condition.operators.in",
    symbol: "∈",
  },
  {
    value: "not_in",
    label: "workflowsEditor.node.condition.operators.not_in",
    symbol: "∉",
  },
  {
    value: "is_true",
    label: "workflowsEditor.node.condition.operators.is_true",
    symbol: <CheckIcon className="size-3" />,
    noValue: true,
  },
  {
    value: "is_false",
    label: "workflowsEditor.node.condition.operators.is_false",
    symbol: <XIcon className="size-3" />,
    noValue: true,
  },
  {
    value: "array_contains_any",
    label: "workflowsEditor.node.condition.operators.array_contains_any",
    defaultLabel: "Contient au moins un",
    symbol: "∩?",
  },
  {
    value: "array_contains_all",
    label: "workflowsEditor.node.condition.operators.array_contains_all",
    defaultLabel: "Contient tous",
    symbol: "⊇",
  },
] as const;
